import { useEffect, useState } from "react";
import { Button, Form, Modal } from "react-bootstrap";
import { locationsService } from "../../../services/locations/locations.service";
import { getUserFacingErrorMessage, showToastError, showToastSuccess } from "../../../utils";

export type EditCustomerAssociationLabelRow = {
    id: string;
    customerName: string;
    label?: string | null;
};

export interface EditCustomerAssociationLabelModalProps {
    show: boolean;
    locationId: string;
    association: EditCustomerAssociationLabelRow | null;
    onClose: () => void;
    onSaved: () => void;
}

const EditCustomerAssociationLabelModal = ({
    show,
    locationId,
    association,
    onClose,
    onSaved,
}: EditCustomerAssociationLabelModalProps) => {
    const [label, setLabel] = useState("");
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (!show || !association) {
            setLabel("");
            return;
        }
        setLabel(association.label ?? "");
    }, [show, association]);

    const handleSave = () => {
        if (!association) return;
        setSaving(true);
        locationsService
            .updateCustomerAssociation(locationId, {
                id: association.id,
                label: label.trim(),
            })
            .then(() => {
                showToastSuccess("Label updated.");
                onSaved();
                onClose();
            })
            .catch((e: unknown) => {
                showToastError(getUserFacingErrorMessage(e, "Could not update label."));
            })
            .finally(() => setSaving(false));
    };

    return (
        <Modal show={show} onHide={onClose} centered>
            <Modal.Header closeButton>
                <Modal.Title>Edit Label</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                {association && (
                    <p className="text-muted mb-3">
                        Customer: <strong>{association.customerName}</strong>
                    </p>
                )}
                <Form.Group>
                    <Form.Label>Label</Form.Label>
                    <Form.Control
                        value={label}
                        onChange={(e) => setLabel(e.target.value)}
                        placeholder="Optional"
                        autoFocus
                        disabled={saving}
                    />
                </Form.Group>
            </Modal.Body>
            <Modal.Footer>
                <Button variant="secondary" onClick={onClose} disabled={saving}>
                    Cancel
                </Button>
                <Button variant="primary" onClick={handleSave} disabled={saving || !association}>
                    {saving ? "Updating…" : "Update"}
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

export { EditCustomerAssociationLabelModal };
